import { EmployeeDto } from '@zorro/clients';
import { Switch } from '@zorro/design-system/v2/Switch';
import { IconZorroBankAccount } from '@zorro/shared/assets';
import { Box, Grid, Group, Stack, Text } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { ChangeEvent, useState } from 'react';

import { getBankAccountTypeLabel } from '../../AddEmployerFlow/AddPaymentAccountDetailsStep/AddPaymentAccountDetailsStepUtils';
import { getSensitivePaymentData } from '../paymentsPciDssMasking.utils';
import { BankAccountField } from './BankAccountField';
import { useEmployeeBankAccount } from './hooks/useEmployeeBankAccount';

type ZorroPayBankAccountProps = {
  employeeId: EmployeeDto['id'];
  isEnabled: boolean;
  isDisabled?: boolean;
};

const ZorroPayBankAccount = ({
  employeeId,
  isEnabled,
  isDisabled = false,
}: ZorroPayBankAccountProps) => {
  const [showDetails, setShowDetails] = useState(false);

  const { bankAccount, isFetching } = useEmployeeBankAccount({
    employeeId,
    enabled: isEnabled,
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
    enabled: isEnabled,
  });

  if (isFetching || !bankAccount) return null;

  const billingAddress = [employee?.address, employee?.zipCode, employee?.state]
    .filter(Boolean)
    .join(', ');

  const formattedAccountNumber = getSensitivePaymentData(
    bankAccount.accountNumber,
    !showDetails
  );
  const formattedRoutingNumber = getSensitivePaymentData(
    bankAccount.routingNumber,
    !showDetails
  );

  return (
    <Stack gap="md" maw={439}>
      <Group gap="xs">
        <IconZorroBankAccount />
        <Text fw="var(--font-weight-600)" fz="var(--font-size-14)">
          Your payment account
        </Text>
      </Group>

      <Box
        bg="var(--brand-grayscale-100)"
        p="1.5rem"
        style={{ borderRadius: 'var(--mantine-radius-sm)' }}
      >
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Stack gap="2rem">
              <BankAccountField
                label="Account number"
                value={formattedAccountNumber}
              />
              <BankAccountField
                label="Name on account"
                value={bankAccount.nameOnAccount}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Stack gap="2rem">
              <BankAccountField
                label="Routing number"
                value={formattedRoutingNumber}
              />
              <BankAccountField
                label="Account type"
                value={getBankAccountTypeLabel(bankAccount.bankAccountType)}
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Box>

      <Switch
        label="Show account details"
        disabled={isDisabled}
        checked={showDetails}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setShowDetails(event.currentTarget.checked);
        }}
      />

      {billingAddress && (
        <Text size="sm">
          <Text component="span" fw="var(--font-weight-600)">
            Billing address:
          </Text>{' '}
          {billingAddress}
        </Text>
      )}
    </Stack>
  );
};

export { ZorroPayBankAccount };
